import { useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import {
  SCHOOL_LEVELS,
  SCHOOL_SECTORS,
  STUDENT_STATUSES,
  type Guardian,
  type GuardianRelation,
  type Page,
  type School,
  type SchoolLevel,
  type SchoolSector,
  type StudentRecord,
  type StudentStatus,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

/**
 * Schools directory (packages/modules/m28-education/src/routes/schools.ts)
 * plus student records (routes/students.ts) — a student always belongs to a
 * school, so both live under one nav item. Direct school registration is
 * for GOVERNMENT/GRANT_AIDED schools only; PRIVATE schools are created by
 * an APPROVED PrivateSchoolLicence (see the Licensing screen).
 */
export function Schools() {
  const [tab, setTab] = useState<'schools' | 'students'>('schools');
  return (
    <div>
      <PageHeader title="Schools & Students" subtitle="Government/grant-aided school directory and the pupil roll." />
      <Tabs tabs={[{ key: 'schools', label: 'Schools' }, { key: 'students', label: 'Students' }]} value={tab} onChange={setTab} />
      {tab === 'schools' ? <SchoolsTab /> : <StudentsTab />}
    </div>
  );
}

function SchoolsTab() {
  const { persona } = useAuth();
  const canManage = persona?.roles.includes('ADMISSIONS_OFFICER') ?? false;
  const [district, setDistrict] = useState('');
  const [level, setLevel] = useState('');
  const [sector, setSector] = useState('');
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (district) qs.set('district', district);
  if (level) qs.set('level', level);
  if (sector) qs.set('sector', sector);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<School>>(`/api/v1/m28/schools?${qs.toString()}`);

  return (
    <div>
      {!canManage ? (
        <div className="mb-4">
          <ErrorNote message="Registering schools or changing capacity requires the Admissions Officer role — the directory below is visible to everyone." />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <input className="field-input !w-48" placeholder="Search name…" value={q} onChange={(e) => setQ(e.target.value)} />
          <input className="field-input !w-40" placeholder="District…" value={district} onChange={(e) => setDistrict(e.target.value)} />
          <select className="field-input !w-40" value={level} onChange={(e) => setLevel(e.target.value)}>
            <option value="">All levels</option>
            {SCHOOL_LEVELS.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
          <select className="field-input !w-40" value={sector} onChange={(e) => setSector(e.target.value)}>
            <option value="">All sectors</option>
            {SCHOOL_SECTORS.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </div>
        {canManage ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Register school
          </button>
        ) : null}
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No schools match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Name</th>
                <th className="table-head">District</th>
                <th className="table-head">Level</th>
                <th className="table-head">Sector</th>
                <th className="table-head text-right">Capacity</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((s) => (
                <tr key={s.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(s.id)}>
                  <td className="table-cell font-medium">{s.name}</td>
                  <td className="table-cell">{s.district}</td>
                  <td className="table-cell">
                    <StatusBadge status={s.level} />
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={s.sector} />
                  </td>
                  <td className="table-cell text-right tabular-nums">{s.capacity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateSchoolModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
      {detailId ? (
        <SchoolDetailModal id={detailId} canManage={canManage} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} />
      ) : null}
    </div>
  );
}

function CreateSchoolModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [district, setDistrict] = useState('');
  const [level, setLevel] = useState<SchoolLevel>('PRIMARY');
  const [sector, setSector] = useState<'GOVERNMENT' | 'GRANT_AIDED'>('GOVERNMENT');
  const [address, setAddress] = useState('');
  const [capacity, setCapacity] = useState('60');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m28/schools', {
        name,
        district,
        level,
        sector,
        address,
        capacity: Number.parseInt(capacity, 10),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register school');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register school" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-400">
          Only GOVERNMENT and GRANT_AIDED schools are registered directly — PRIVATE schools are created automatically once a licence
          application is approved (see Private School Licensing).
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name">
            <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Grand Anse Mahé Primary School" />
          </Field>
          <Field label="District">
            <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="e.g. grand-anse-mahe" />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Level">
            <select className="field-input" value={level} onChange={(e) => setLevel(e.target.value as SchoolLevel)}>
              {SCHOOL_LEVELS.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Sector">
            <select className="field-input" value={sector} onChange={(e) => setSector(e.target.value as 'GOVERNMENT' | 'GRANT_AIDED')}>
              <option value="GOVERNMENT">GOVERNMENT</option>
              <option value="GRANT_AIDED">GRANT_AIDED</option>
            </select>
          </Field>
          <Field label="Capacity">
            <input className="field-input" type="number" min="1" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
          </Field>
        </div>
        <Field label="Address">
          <input className="field-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="e.g. Grand Anse, Mahé" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || name.trim().length < 2 || district.trim().length < 2 || address.trim().length < 5}
          >
            {busy ? 'Registering…' : 'Register school'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SchoolDetailModal({ id, canManage, onClose, onChanged }: { id: string; canManage: boolean; onClose: () => void; onChanged: () => void }) {
  const detailQ = useApiQuery<School>(`/api/v1/m28/schools/${id}`);
  const [capacity, setCapacity] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const school = detailQ.data;

  const updateCapacity = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/v1/m28/schools/${id}/capacity`, { capacity: Number.parseInt(capacity, 10) });
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not update capacity');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="School detail" onClose={onClose}>
      {detailQ.loading || !school ? (
        <Spinner />
      ) : (
        <div className="space-y-3 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex flex-wrap gap-1.5">
            <StatusBadge status={school.level} />
            <StatusBadge status={school.sector} />
          </div>
          <dl className="space-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">Name</dt>
              <dd className="font-medium">{school.name}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">District</dt>
              <dd className="font-medium">{school.district}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Address</dt>
              <dd className="font-medium">{school.address}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Capacity</dt>
              <dd className="font-medium tabular-nums">{school.capacity}</dd>
            </div>
          </dl>
          {canManage ? (
            <div className="flex items-end gap-2 border-t border-slate-100 pt-3">
              <Field label="New capacity" className="!mb-0 flex-1">
                <input className="field-input" type="number" min="1" placeholder={String(school.capacity)} value={capacity} onChange={(e) => setCapacity(e.target.value)} />
              </Field>
              <button className="btn-primary" disabled={busy || !capacity} onClick={updateCapacity}>
                Update
              </button>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

function StudentsTab() {
  const { persona } = useAuth();
  const isStaff = (persona?.roles.includes('SCHOOL_ADMIN') || persona?.roles.includes('ADMISSIONS_OFFICER')) ?? false;
  const [status, setStatus] = useState('');
  const [schoolId, setSchoolId] = useState('');
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (status) qs.set('status', status);
  if (schoolId) qs.set('schoolId', schoolId);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<StudentRecord>>(isStaff ? `/api/v1/m28/students?${qs.toString()}` : null);
  const schoolsQ = useApiQuery<Page<School>>('/api/v1/m28/schools?limit=100');

  if (!isStaff) {
    return <ErrorNote message="Listing student records requires the School Administrator or Admissions Officer role." />;
  }

  const schoolName = (id: string | null) => schoolsQ.data?.items.find((s) => s.id === id)?.name ?? id ?? '—';

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <input className="field-input !w-52" placeholder="Search NIN / name…" value={q} onChange={(e) => setQ(e.target.value)} />
          <input className="field-input !w-40" placeholder="School id…" value={schoolId} onChange={(e) => setSchoolId(e.target.value)} />
          <select className="field-input !w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STUDENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>
          + Register student
        </button>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No student records match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">NIN</th>
                <th className="table-head">Name</th>
                <th className="table-head">DOB</th>
                <th className="table-head">Year group</th>
                <th className="table-head">School</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((s) => (
                <tr key={s.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(s.id)}>
                  <td className="table-cell font-mono text-xs">{s.nin}</td>
                  <td className="table-cell font-medium">{s.fullName}</td>
                  <td className="table-cell">{formatDate(s.dob)}</td>
                  <td className="table-cell">{s.yearGroup}</td>
                  <td className="table-cell text-xs text-slate-500">{schoolName(s.currentSchoolId)}</td>
                  <td className="table-cell">
                    <StatusBadge status={s.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateStudentModal
          schools={schoolsQ.data?.items ?? []}
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
      {detailId ? (
        <StudentDetailModal
          id={detailId}
          schools={schoolsQ.data?.items ?? []}
          onClose={() => setDetailId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

const GUARDIAN_RELATIONS: GuardianRelation[] = ['MOTHER', 'FATHER', 'GUARDIAN', 'OTHER'];

function CreateStudentModal({ schools, onClose, onDone }: { schools: School[]; onClose: () => void; onDone: () => void }) {
  const [nin, setNin] = useState('');
  const [fullName, setFullName] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState<'M' | 'F'>('M');
  const [currentSchoolId, setCurrentSchoolId] = useState('');
  const [yearGroup, setYearGroup] = useState('P1');
  const [guardians, setGuardians] = useState<Guardian[]>([{ nin: '', relation: 'GUARDIAN', primaryContact: true }]);
  const [senFlags, setSenFlags] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const updateGuardian = (idx: number, patch: Partial<Guardian>) => {
    setGuardians((gs) => gs.map((g, i) => (i === idx ? { ...g, ...patch } : g)));
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m28/students', {
        nin,
        fullName,
        dob,
        gender,
        currentSchoolId: currentSchoolId || undefined,
        yearGroup,
        guardians: guardians.filter((g) => g.nin.trim().length > 0),
        senFlags: senFlags
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register student');
    } finally {
      setBusy(false);
    }
  };

  const validGuardians = guardians.some((g) => g.nin.trim().length >= 5);

  return (
    <Modal title="Register student" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Student NIN">
            <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} placeholder="e.g. SC160304012" />
          </Field>
          <Field label="Full name">
            <input className="field-input" value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Date of birth">
            <input className="field-input" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
          </Field>
          <Field label="Gender">
            <select className="field-input" value={gender} onChange={(e) => setGender(e.target.value as 'M' | 'F')}>
              <option value="M">M</option>
              <option value="F">F</option>
            </select>
          </Field>
          <Field label="Year group">
            <input className="field-input" value={yearGroup} onChange={(e) => setYearGroup(e.target.value)} placeholder="e.g. P1, S3" />
          </Field>
        </div>
        <Field label="Current school (optional)">
          <select className="field-input" value={currentSchoolId} onChange={(e) => setCurrentSchoolId(e.target.value)}>
            <option value="">— None —</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Guardians (at least one)">
          <div className="space-y-2">
            {guardians.map((g, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  className="field-input flex-1"
                  placeholder="Guardian NIN"
                  value={g.nin}
                  onChange={(e) => updateGuardian(idx, { nin: e.target.value })}
                />
                <select className="field-input !w-36" value={g.relation} onChange={(e) => updateGuardian(idx, { relation: e.target.value as GuardianRelation })}>
                  {GUARDIAN_RELATIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                <label className="flex items-center gap-1 text-xs text-slate-600">
                  <input type="checkbox" checked={g.primaryContact} onChange={(e) => updateGuardian(idx, { primaryContact: e.target.checked })} />
                  Primary
                </label>
                {guardians.length > 1 ? (
                  <button className="text-xs text-rose-500" onClick={() => setGuardians((gs) => gs.filter((_, i) => i !== idx))}>
                    Remove
                  </button>
                ) : null}
              </div>
            ))}
            <button
              className="btn-secondary !px-2.5 !py-1 text-xs"
              onClick={() => setGuardians((gs) => [...gs, { nin: '', relation: 'GUARDIAN', primaryContact: false }])}
            >
              + Add guardian
            </button>
          </div>
        </Field>

        <Field label="SEN flags (comma separated, optional)">
          <input className="field-input" value={senFlags} onChange={(e) => setSenFlags(e.target.value)} placeholder="e.g. DYSLEXIA, HEARING_IMPAIRMENT" />
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || nin.trim().length < 5 || fullName.trim().length < 2 || !dob || !yearGroup.trim() || !validGuardians}
          >
            {busy ? 'Registering…' : 'Register student'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function StudentDetailModal({
  id,
  schools,
  onClose,
  onChanged,
}: {
  id: string;
  schools: School[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const detailQ = useApiQuery<StudentRecord>(`/api/v1/m28/students/${id}`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<StudentStatus | ''>('');
  const [currentSchoolId, setCurrentSchoolId] = useState('');
  const [senFlags, setSenFlags] = useState('');

  const student = detailQ.data;

  const patch = async () => {
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {};
      if (status) body.status = status;
      if (currentSchoolId) body.currentSchoolId = currentSchoolId;
      await api.patch(`/api/v1/m28/students/${id}`, body);
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not update student record');
    } finally {
      setBusy(false);
    }
  };

  const updateSen = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/v1/m28/students/${id}/sen`, {
        senFlags: senFlags
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      });
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not update SEN flags');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Student record" onClose={onClose} wide>
      {detailQ.loading || !student ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex items-center gap-2">
            <StatusBadge status={student.status} />
            <span className="font-mono text-xs text-slate-500">{student.nin}</span>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">Name</dt>
              <dd className="font-medium">{student.fullName}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">DOB</dt>
              <dd className="font-medium">{formatDate(student.dob)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Gender</dt>
              <dd className="font-medium">{student.gender}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Year group</dt>
              <dd className="font-medium">{student.yearGroup}</dd>
            </div>
          </dl>
          <div className="border-t border-slate-100 pt-3">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Guardians</div>
            <ul className="space-y-1 text-xs">
              {student.guardians.map((g, i) => (
                <li key={i} className="flex justify-between rounded-lg border border-slate-200 px-2.5 py-1.5">
                  <span className="font-mono">{g.nin}</span>
                  <span className="text-slate-500">
                    {g.relation}
                    {g.primaryContact ? ' · primary' : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          {student.senFlags ? (
            <div className="border-t border-slate-100 pt-3">
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">SEN flags</div>
              <div className="flex flex-wrap gap-1.5">
                {student.senFlags.length === 0 ? <span className="text-xs text-slate-400">None declared</span> : null}
                {student.senFlags.map((f) => (
                  <span key={f} className="rounded bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700 ring-1 ring-violet-200">
                    {f}
                  </span>
                ))}
              </div>
              <div className="mt-2 flex items-end gap-2">
                <Field label="Update SEN flags (comma separated)" className="!mb-0 flex-1">
                  <input className="field-input" value={senFlags} onChange={(e) => setSenFlags(e.target.value)} placeholder={student.senFlags.join(', ')} />
                </Field>
                <button className="btn-secondary" disabled={busy} onClick={updateSen}>
                  Save
                </button>
              </div>
            </div>
          ) : null}
          <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-3">
            <Field label="Status" className="!mb-0">
              <select className="field-input !w-40" value={status} onChange={(e) => setStatus(e.target.value as StudentStatus)}>
                <option value="">— unchanged —</option>
                {STUDENT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Move to school" className="!mb-0">
              <select className="field-input !w-56" value={currentSchoolId} onChange={(e) => setCurrentSchoolId(e.target.value)}>
                <option value="">— unchanged —</option>
                {schools.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </Field>
            <button className="btn-primary" disabled={busy || (!status && !currentSchoolId)} onClick={patch}>
              Save changes
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
